import { TaskTypeString } from 'lib/constants/task'
import { TaskActionType, TaskType, TaskTypeAction, TaskTypeCertify, TaskTypeGrant, TaskTypeRevoke } from 'pbts/c1/api/task/v1/task'
import { IntlShape, FormattedMessage } from 'react-intl'

export const GrantTaskType = TaskType.create({
    taskType: {
        oneofKind: 'grant',
        grant: TaskTypeGrant.create({}),
    },
})

export const RevokeTaskType = TaskType.create({
    taskType: {
        oneofKind: 'revoke',
        revoke: TaskTypeRevoke.create({}),
    },
})

export const CertifyTaskType = TaskType.create({
    taskType: {
        oneofKind: 'certify',
        certify: TaskTypeCertify.create({}),
    },
})

export const ActionTaskType = TaskType.create({
    taskType: {
        oneofKind: 'action',
        action: TaskTypeAction.create({}),
    },
})

// Returns the enum string representation.
export const taskTypeToString = (tt?: TaskType): string => {
    // TODO(Logan): Figure out how to get this from TaskTypeGrant object
    // I've already spent too much time trying to figure it out today
    switch (tt?.taskType.oneofKind) {
        case GrantTaskType.taskType.oneofKind:
            return TaskTypeString.Grant
        case RevokeTaskType.taskType.oneofKind:
            return TaskTypeString.Revoke
        case CertifyTaskType.taskType.oneofKind:
            return TaskTypeString.Certify
        case ActionTaskType.taskType.oneofKind:
            return TaskTypeString.Action
        default:
            return TaskTypeString.Invalid
    }
}

export const taskTypeFromString = (taskTypeStr: string): TaskType | undefined => {
    switch (taskTypeStr) {
        case TaskTypeString.Grant:
            return GrantTaskType
        case TaskTypeString.Revoke:
            return RevokeTaskType
        case TaskTypeString.Certify:
            return CertifyTaskType
        case TaskTypeString.Action:
            return ActionTaskType
        // no default
    }
    return undefined
}

export const taskTypeToListString = (tt?: TaskType): React.ReactElement => {
    const invalid = <FormattedMessage id="task_type_to_list_string_invalid" defaultMessage="Invalid" />
    if (!tt) {
        return invalid
    }
    switch (tt.taskType.oneofKind) {
        case GrantTaskType.taskType.oneofKind:
            return <FormattedMessage id="task_type_to_list_string_grant" defaultMessage="Grant" />
        case RevokeTaskType.taskType.oneofKind:
            return <FormattedMessage id="task_type_to_list_string_deprovision" defaultMessage="Revoke" />
        case CertifyTaskType.taskType.oneofKind:
            return <FormattedMessage id="task_type_to_list_string_review" defaultMessage="Review" />
        case ActionTaskType.taskType.oneofKind:
            return <FormattedMessage id="task_type_to_list_string_action" defaultMessage="Action" />
        default:
            return invalid
    }
}

// Returns the internationalized human-readable string for the given type.
export const taskTypeToIntl = (tt: TaskType | undefined, intl: IntlShape): string => {
    const unknown = intl.formatMessage({ id: 'Components_Tasks_Type_Unknown', defaultMessage: 'Unknown' })
    if (!tt) {
        return unknown
    }
    switch (tt.taskType.oneofKind) {
        case GrantTaskType.taskType.oneofKind:
            return intl.formatMessage({ id: 'Components_Tasks_Type_Request', defaultMessage: 'Request' })
        case RevokeTaskType.taskType.oneofKind:
            return intl.formatMessage({
                id: 'Components_Tasks_Type_Revoke',
                defaultMessage: 'Revoke',
            })
        case CertifyTaskType.taskType.oneofKind:
            return intl.formatMessage({
                id: 'Components_Tasks_Type_Review',
                defaultMessage: 'Review',
            })
        case ActionTaskType.taskType.oneofKind:
            return intl.formatMessage({
                id: 'Components_Tasks_Type_Action',
                defaultMessage: 'Action',
            })
        default:
            return unknown
    }
}

// Returns a <FormattedMessage /> that describes the given action type. For use in dropdowns or buttons that trigger actions
// ex: TaskActionType.REASSIGN -> Reassign to...
// Assumes that tasks are of type 'grant'
export const taskActionTypeToFormattedMessageMap = new Map<TaskActionType, React.ReactElement>([
    [TaskActionType.UNSPECIFIED, <FormattedMessage id="task_action_type_unspecified" defaultMessage="Unspecified action" />],
    [TaskActionType.CLOSE, <FormattedMessage id="task_action_type_close" defaultMessage="Cancel" />],
    [TaskActionType.APPROVE, <FormattedMessage id="task_action_type_approve" defaultMessage="Approve" />],
    [TaskActionType.DENY, <FormattedMessage id="task_action_type_deny" defaultMessage="Deny" />],
    [TaskActionType.COMMENT, <FormattedMessage id="task_action_type_deny" defaultMessage="Comment" />],
    [TaskActionType.DELETE, <FormattedMessage id="task_action_type_delete" defaultMessage="Delete" />],
    [TaskActionType.REASSIGN, <FormattedMessage id="task_action_type_reassign" defaultMessage="Reassign to..." />],
    [TaskActionType.RESTART, <FormattedMessage id="task_action_type_restart" defaultMessage="Restart" />],
    [TaskActionType.SEND_REMINDER, <FormattedMessage id="task_action_type_reassign" defaultMessage="Send reminder" />],
    [TaskActionType.HARD_RESET, <FormattedMessage id="task_action_type_hard_reset" defaultMessage="Hard reset" />],
    [TaskActionType.CHANGE_POLICY, <FormattedMessage id="task_action_type_change_policy" defaultMessage="Change policy" />],
    [TaskActionType.PROVISION_COMPLETE, <FormattedMessage id="task_action_type_provision_complete" defaultMessage="Mark as provisioned" />],
    [TaskActionType.SKIP_STEP, <FormattedMessage id="task_action_type_skip_step" defaultMessage="Skip step" />],
])
